import type { DocumentOrderBy } from "@common/client"
import { z } from "zod"
import type { ColumnSizing } from "@/lib/column-widths"

export const COLUMNS = [
  "number",
  "emergency",
  "subject",
  "issued_by",
  "source_feed",
  "manufacturer",
  "models",
  "ata",
  "effective",
  "supersedes",
  "superseded_by",
  "added",
] as const

export const columnSchema = z.enum(COLUMNS)

export type Column = z.infer<typeof columnSchema>

export const DEFAULT_COLUMNS: Column[] = [...COLUMNS]

export const COLUMN_LABELS: Record<Column, string> = {
  number: "AD no.",
  emergency: "Emergency",
  subject: "Subject",
  issued_by: "Issued by",
  source_feed: "Source feed",
  manufacturer: "Manufacturer",
  models: "Models",
  ata: "ATA",
  effective: "Effective",
  supersedes: "Supersedes",
  superseded_by: "Superseded by",
  added: "Added",
}

export const SORT_KEYS: Partial<Record<Column, DocumentOrderBy>> = {
  number: "document_number",
  subject: "title",
  issued_by: "issued_by",
  source_feed: "regulator",
  manufacturer: "manufacturer",
  ata: "ata_chapter",
  effective: "effective_date",
  added: "created_at",
}

export const COLUMN_SIZING: ColumnSizing<Slot> = {
  id: "airworthiness-directives",
  widths: {
    number: 8,
    emergency: 1.5,
    subject: 16,
    issued_by: 4.5,
    source_feed: 6,
    manufacturer: 10,
    models: 14.5,
    ata: 4.5,
    effective: 6.5,
    supersedes: 7,
    superseded_by: 7,
    added: 6.5,
    actions: 4,
  },
  fill: "subject",
  pinned: ["emergency", "actions"],
  extras: ["actions"],
  dropOrder: [
    "added",
    "supersedes",
    "superseded_by",
    "source_feed",
    "manufacturer",
    "effective",
    "ata",
    "issued_by",
    "models",
    "emergency",
  ],
}

export type Slot = Column | "actions"

export function withNumber(columns: Column[]): Column[] {
  return COLUMNS.filter(
    (column) => column === "number" || columns.includes(column),
  )
}

export function slotsOf(columns: Column[]): Slot[] {
  return [...withNumber(columns), "actions"]
}
